import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthStore } from '../store/authStore';
import supabase from '../lib/supabase';
import type { Message, Conversation } from '../types';

export function useMessaging(conversationId?: string, onConversationUpdate?: () => void) {
  const { user } = useAuthStore();
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  
  // Polling fallback
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastPolledTimestamp = useRef<string | null>(null);

  // Polling fallback for when real-time fails
  const pollForNewMessages = useCallback(async () => {
    if (!conversationId || !user) return;

    try {
      console.log('🔄 [POLLING] Checking for new messages...');
      
      const pollTimestamp = lastPolledTimestamp.current || 
        new Date(Date.now() - 5000).toISOString(); // Last 5 seconds
      
      const { data, error } = await supabase
        .from('messages')
        .select(`
          *,
          sender:sender_id(
            id,
            full_name,
            display_name,
            verified,
            avatar_url
          ),
          read_by:message_read_receipts(
            user_id
          )
        `)
        .eq('conversation_id', conversationId)
        .gte('created_at', pollTimestamp)
        .order('created_at', { ascending: true });

      if (error) throw error;

      if (data && data.length > 0) {
        console.log('📨 [POLLING] Found new messages:', data.length);
        
        setMessages(prev => {
          const newMessages = data.filter(newMsg => 
            !prev.some(existingMsg => existingMsg.id === newMsg.id)
          );
          
          if (newMessages.length > 0) {
            console.log('✅ [POLLING] Adding new messages:', newMessages.length);
            
            // Mark new messages as read if from other users
            const unreadMessages = newMessages.filter(
              msg => msg.sender_id !== user.id && !msg.read_by?.some(r => r.user_id === user.id)
            );

            if (unreadMessages.length > 0) {
              supabase
                .from('message_read_receipts')
                .insert(
                  unreadMessages.map(msg => ({
                    message_id: msg.id,
                    user_id: user.id
                  }))
                );
            }
            
            // Trigger conversation list update
            if (onConversationUpdate) {
              console.log('🔄 [POLLING] Triggering conversation list update');
              onConversationUpdate();
            }
            
            return [...prev, ...newMessages];
          }
          
          return prev;
        });
      }
      
      lastPolledTimestamp.current = new Date().toISOString();
      
    } catch (err) {
      console.error('❌ [POLLING] Error:', err);
    }
  }, [conversationId, user, onConversationUpdate]);

  // Mark conversation as read
  const markConversationAsRead = useCallback(async () => {
    if (!conversationId || !user) return;

    try {
      // Get conversation details to determine if it's a group
      const { data: conversation } = await supabase
        .from('conversations')
        .select('is_group, user1_id, user2_id')
        .eq('id', conversationId)
        .single();

      if (!conversation) return;

      if (conversation.is_group) {
        // For group chats, update participant's last_read_at
        await supabase
          .from('conversation_participants')
          .update({ 
            last_read_at: new Date().toISOString() 
          })
          .eq('conversation_id', conversationId)
          .eq('user_id', user.id);
      } else {
        // For direct messages, update conversation read timestamp
        const updateField = conversation.user1_id === user.id 
          ? 'last_read_by_user1' 
          : 'last_read_by_user2';
        
        await supabase
          .from('conversations')
          .update({ 
            [updateField]: new Date().toISOString() 
          })
          .eq('id', conversationId);
      }

      // Trigger conversation list update to remove unread indicators
      if (onConversationUpdate) {
        onConversationUpdate();
      }
    } catch (error) {
      console.error('Error marking conversation as read:', error);
    }
  }, [conversationId, user, onConversationUpdate]);

  // Fetch messages with pagination
  const fetchMessages = useCallback(async (lastMessageId?: string) => {
    if (!conversationId || !user) return;

    try {
      setError(null);
      let query = supabase
        .from('messages')
        .select(`
          *,
          sender:sender_id(
            id,
            full_name,
            display_name,
            verified,
            avatar_url
          ),
          read_by:message_read_receipts(
            user_id
          )
        `)
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (lastMessageId) {
        query = query.lt('id', lastMessageId);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      const newMessages = data || [];
      setMessages(prev => 
        lastMessageId 
          ? [...prev, ...newMessages.reverse()]
          : newMessages.reverse()
      );
      setHasMore(newMessages.length === 50);

      // Mark messages as read
      const unreadMessages = newMessages.filter(
        msg => msg.sender_id !== user.id && !msg.read_by?.some(r => r.user_id === user.id)
      );

      if (unreadMessages.length > 0) {
        const { error: readError } = await supabase
          .from('message_read_receipts')
          .insert(
            unreadMessages.map(msg => ({
              message_id: msg.id,
              user_id: user.id
            }))
          );

        if (readError) console.error('Error marking messages as read:', readError);
      }

      // Mark conversation as read when viewing messages
      await markConversationAsRead();
    } catch (err: any) {
      console.error('Error fetching messages:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [conversationId, user]);

  // Edit message
  const editMessage = async (messageId: string, content: string) => {
    if (!user || !conversationId) return;

    try {
      // Update the message
      const { data: message, error: updateError } = await supabase
        .from('messages')
        .update({ 
          content,
          is_edited: true,
          edited_at: new Date().toISOString()
        })
        .eq('id', messageId)
        .eq('sender_id', user.id)
        .select()
        .single();

      if (updateError) throw updateError;

      // Update local messages state
      setMessages(prev => prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, content, is_edited: true, edited_at: new Date().toISOString() }
          : msg
      ));

      // Update conversation last message if this was the most recent
      const isLatestMessage = messages[messages.length - 1]?.id === messageId;
      if (isLatestMessage) {
        const { error: convError } = await supabase
          .from('conversations')
          .update({
            last_message: content,
            updated_at: new Date().toISOString()
          })
          .eq('id', conversationId);

        if (convError) throw convError;
      }

      return message;
    } catch (err: any) {
      console.error('Error editing message:', err);
      throw err;
    }
  };

  // Send message with retry logic
  const sendMessage = async (content: string, attachments?: any[]) => {
    if (!conversationId || !user) return;

    const maxRetries = 3;
    let retryCount = 0;

    while (retryCount < maxRetries) {
      try {
        // Insert the message
        const { data, error: insertError } = await supabase
          .from('messages')
          .insert({
            conversation_id: conversationId,
            sender_id: user.id,
            content,
            attachments
          })
          .select(`
            *,
            sender:sender_id(
              id,
              full_name,
              display_name,
              verified,
              avatar_url
            )
          `)
          .single();

        if (insertError) throw insertError;
        
        // Update conversation last message
        const { error: updateError } = await supabase
          .from('conversations')
          .update({
            last_message: content,
            last_message_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', conversationId);

        if (updateError) throw updateError;
          
        setMessages(prev => [...prev, data]);
        return;
      } catch (err: any) {
        console.error(`Send message attempt ${retryCount + 1} failed:`, err);
        
        // Check for specific RLS policy violation (blocking)
        if (err.code === '42501' && err.message?.includes('row-level security policy')) {
          throw new Error('You cannot send a message to this user due to a blocking relationship.');
        }
        
        retryCount++;
        
        if (retryCount === maxRetries) {
          throw new Error('Failed to send message after multiple attempts');
        }
        
        // Wait before retrying (only for non-blocking errors)
        await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
      }
    }
  };

  // Subscribe to new messages
  useEffect(() => {
    if (!conversationId || !user) return;

    console.log('🚀 Setting up real-time subscription for:', conversationId);

    const subscription = supabase
      .channel(`messages:${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`
        },
        async (payload) => {
          console.log('📡 Real-time event received:', payload.eventType, payload);
          
          if (payload.eventType === 'INSERT') {
            const message = payload.new;
            // Fetch sender details
            const { data: sender } = await supabase
              .from('user_profiles')
              .select('id, full_name, display_name, verified, avatar_url')
              .eq('id', message.sender_id)
              .single();

            console.log('✅ Adding new message via real-time:', message.id);
            setMessages(prev => [...prev, { ...message, sender }]);

            // Mark message as read if from another user
            if (message.sender_id !== user.id) {
              await supabase
                .from('message_read_receipts')
                .insert({
                  message_id: message.id,
                  user_id: user.id
                });
            }

            // Trigger conversation list update
            if (onConversationUpdate) {
              console.log('🔄 Triggering conversation list update');
              onConversationUpdate();
            }
          } else if (payload.eventType === 'UPDATE') {
            console.log('✏️ Updating message via real-time:', payload.new.id);
            setMessages(prev => prev.map(msg =>
              msg.id === payload.new.id ? { ...msg, ...payload.new } : msg
            ));
            
            // Trigger conversation list update for edits too
            if (onConversationUpdate) {
              console.log('🔄 Triggering conversation list update for edit');
              onConversationUpdate();
            }
          }
        }
      )
      .subscribe((status) => {
        console.log('📡 Subscription status:', status);
        
        if (status === 'SUBSCRIBED') {
          console.log('✅ Real-time working, stopping polling');
          // Clear polling if real-time starts working
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
        } else if (status === 'CHANNEL_ERROR' || status === 'CLOSED') {
          console.log('⚠️ Real-time failed, starting polling fallback');
          // Start polling fallback
          if (!pollIntervalRef.current) {
            lastPolledTimestamp.current = new Date().toISOString();
            pollIntervalRef.current = setInterval(pollForNewMessages, 2000); // Poll every 2 seconds
          }
        }
      });

    return () => {
      console.log('🧹 Cleaning up subscription and polling');
      subscription.unsubscribe();
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [conversationId, user, onConversationUpdate, pollForNewMessages]);

  // Subscribe to conversation updates
  useEffect(() => {
    if (!conversationId || !user) return;

    const subscription = supabase
      .channel(`conversation:${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversations',
          filter: `id=eq.${conversationId}`
        },
        (payload) => {
          if (payload.eventType === 'UPDATE') {
            setConversation(prev => prev ? { ...prev, ...payload.new } : null);
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [conversationId, user]);

  // Initial data fetch
  useEffect(() => {
    if (conversationId) {
      fetchMessages();
    }
  }, [conversationId, fetchMessages]);

  return {
    messages,
    conversation,
    isLoading,
    error,
    hasMore,
    sendMessage,
    editMessage,
    loadMoreMessages: () => fetchMessages(messages[0]?.id),
  };
}